/**
 * Environment-based visibility configuration for AppsSidebar
 * 
 * Controls which sections and app categories are visible based on the environment.
 * This is independent of user role checks (those still apply when configured).
 * 
 * The user profile section (bottom area with user info, language, theme, logout)
 * is ALWAYS visible regardless of environment.
 */

export type Environment = 'development' | 'production';

export interface SectionVisibility {
  notifications: boolean;
  reports: boolean;
  store: boolean;
}

export interface EnvironmentVisibilityConfig {
  sections: SectionVisibility;
  /** 
   * List of category IDs that are visible in this environment.
   * Categories not in this list will be hidden.
   * Role-based checks still apply on top of this.
   */
  visibleCategories: string[];
}

type VisibilityConfigMap = Record<Environment, EnvironmentVisibilityConfig>;

/**
 * Visibility configuration per environment
 * 
 * Categories available:
 * - 'rastreo-telemetria': Fleet management / GPS tracking
 * - 'administracion': Administration (billing, services, etc.)
 * - 'point-of-sale': POS system
 * - 'internos': Internal systems (requires role check)
 */
export const VISIBILITY_CONFIG: VisibilityConfigMap = {
  development: {
    sections: {
      notifications: true,
      reports: true,
      store: true,
    },
    visibleCategories: [
      'rastreo-telemetria',
      'administracion',
      'point-of-sale',
      'internos',
    ],
  },
  production: {
    sections: {
      notifications: true, // Alertas - siempre visible en prod
      reports: true,
      store: false,
    },
    visibleCategories: [
      'rastreo-telemetria', // Gestión de flota
      'internos',           // Sistemas internos (con validación de role)
    ],
  },
};

/**
 * Gets the current environment from Vite's import.meta.env
 */
export const getCurrentEnvironment = (): Environment => {
  const mode = import.meta.env.MODE;
  return mode === 'production' ? 'production' : 'development';
};

/**
 * Gets the visibility configuration for the current environment
 */
export const getVisibilityConfig = (): EnvironmentVisibilityConfig => {
  const env = getCurrentEnvironment();
  return VISIBILITY_CONFIG[env];
};

/**
 * Checks if a specific category should be visible in the current environment
 */
export const isCategoryVisible = (categoryId: string): boolean => {
  const config = getVisibilityConfig();
  return config.visibleCategories.includes(categoryId);
};

/**
 * Checks if a specific section should be visible in the current environment
 */
export const isSectionVisible = (section: keyof SectionVisibility): boolean => {
  const config = getVisibilityConfig();
  return config.sections[section];
};

/**
 * Filters categories based on environment visibility
 * Role-based filtering should be applied separately after this
 */
export const filterCategoriesByEnvironment = <T extends { id: string }>(
  categories: T[]
): T[] => {
  const config = getVisibilityConfig();
  return categories.filter(category => 
    config.visibleCategories.includes(category.id)
  );
};
